import * as React from 'react';
import { isPwaInstalled } from '@lib/pwa/install-utils';

/**
 * Hook to detect if a media query matches.
 * Useful for responsive component behavior.
 *
 * @param query - CSS media query string (e.g., '(min-width: 768px)')
 * @returns boolean indicating if the media query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState<boolean>(() => {
    // Check if window is available (SSR safety)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Set initial value
    setMatches(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * Hook to detect if the app is running as a PWA (installed app).
 * Returns true if running in standalone mode (installed PWA).
 * On desktop/Android, reactively updates via matchMedia if the user installs mid-session.
 * On iOS Safari, `navigator.standalone` is captured once on mount — it cannot change
 * mid-session (installing requires closing Safari and launching from home screen).
 */
export function useIsPWA(): boolean {
  const isStandalone = useMediaQuery('(display-mode: standalone)');
  const [iosSafariStandalone] = React.useState(() => isPwaInstalled() && !isStandalone);
  return isStandalone || iosSafariStandalone;
}
